import { SubscriptionTiers } from "../../features/user/types";
import appLogger from "../../infra/logger/appLogger";
import { localStorageService } from "../../services/index";
import { CMS_BANNERS_STORAGE_KEY } from "./constants";
import { BannerType, CONTENTFUL_SCREENS } from "./types";
const cmsLogger = appLogger.withTag("Cms");
export const getCustomizeBannerActions = (bannerActions = []) => {
    const customizedBannerActions = [];
    bannerActions.forEach((action) => {
        const customizedAction = {
            label: action?.text?.label,
            fontSize: action?.text?.fontSize,
            fontWeight: action?.text?.fontWeight,
            color: action?.text?.color,
            shape: action?.shape,
            backgroundColor: action?.backgroundColor,
            hoverBackgroundColor: action?.hoverBackgroundColor,
            labelHoverColor: action?.labelHoverColor,
            navigation: {
                type: action?.navigation?.type,
                destination: action?.navigation?.destination,
            },
            placement: action?.placement,
        };
        customizedBannerActions.push(customizedAction);
    });
    return customizedBannerActions;
};
export const getCustomizeBannerStructure = (banner) => {
    const inlineBannerRowIndex = banner?.placement === BannerType.Inline ? banner?.inlineBannerRowIndex : undefined;
    const customizedBanner = {
        name: banner?.name,
        screen: banner?.screen,
        version: banner?.version,
        backgroundColor: banner?.backgroundColor,
        backgroundImageUrl: banner?.backgroundImageUrl,
        closable: banner?.closable,
        title: {
            label: banner?.title?.label,
            fontSize: banner?.title?.fontSize,
            fontWeight: banner?.title?.fontWeight,
            color: banner?.title?.color,
        },
        description: {
            label: banner?.description?.label,
            fontSize: banner?.description?.fontSize,
            color: banner?.description?.color,
            fontWeight: banner?.description?.fontWeight,
        },
        additionalImage: {
            url: banner?.additionalImage?.url,
            placement: banner?.additionalImage?.placement,
        },
        placement: banner?.placement,
        platforms: banner?.platforms,
        inlineBannerRowIndex,
        contentPlacement: banner?.contentPlacement,
        actions: getCustomizeBannerActions(banner?.actions),
        backgroundAction: banner?.backgroundAction,
        scheduling: {
            startDateAndTime: banner?.scheduling?.startDateAndTime,
            endDateAndTime: banner?.scheduling?.endDateAndTime,
        },
        transitionTime: banner?.transitionTime ?? 5,
    };
    return customizedBanner;
};
export const isBannerScheduledForNow = (scheduling) => {
    if (!scheduling)
        return true;
    const { startDateAndTime, endDateAndTime } = scheduling;
    const now = new Date();
    // If both dates are missing, the banner is always active
    if (!startDateAndTime && !endDateAndTime)
        return true;
    try {
        // If only start date is provided, check if current time is after start
        if (startDateAndTime && !endDateAndTime) {
            const start = new Date(startDateAndTime);
            return now >= start;
        }
        // If only end date is provided, check if current time is before end
        if (!startDateAndTime && endDateAndTime) {
            const end = new Date(endDateAndTime);
            return now <= end;
        }
        // If both dates are provided, check if current time is within range
        const start = new Date(startDateAndTime);
        const end = new Date(endDateAndTime);
        return now >= start && now <= end;
    }
    catch (error) {
        // Log error and default to showing the banner in case of parsing errors
        cmsLogger.error("Error parsing banner schedule dates:", error);
        return true;
    }
};
export const getUserPropertiesPayload = (isAuthenticated, subscriptionTier, billingCycle) => {
    let payload = {};
    if (!isAuthenticated) {
        payload = { subscription_tier: SubscriptionTiers.ANONYMOUS };
    }
    else if ([SubscriptionTiers.BASIC, SubscriptionTiers.MINIMAL, SubscriptionTiers.PREMIUM].includes(subscriptionTier) && billingCycle) {
        payload = {
            subscription_tier: subscriptionTier,
            billing_cycle: billingCycle,
        };
    }
    else {
        payload = { subscription_tier: subscriptionTier };
    }
    return payload;
};
export const groupBannersByScreen = (banners) => {
    const bannerDetails = {};
    banners?.forEach((banner) => {
        banner?.screen?.forEach((bannerScreen) => {
            const bannersForScreen = bannerDetails[bannerScreen] || [];
            bannersForScreen.push(banner);
            bannerDetails[bannerScreen] = bannersForScreen;
        });
    });
    return bannerDetails;
};
export const getGroupedBanners = (banners = []) => {
    if (!Array.isArray(banners)) {
        return {};
    }
    const filterBannersToShow = banners?.filter((banner) => isPreviewBanner(banner?.name) || isBannerScheduledForNow(banner?.scheduling));
    const groupBannerByScreens = groupBannersByScreen(filterBannersToShow);
    Object.keys(groupBannerByScreens).forEach((key) => {
        const banners = groupBannerByScreens[key];
        groupBannerByScreens[key] = getGroupedBannersByPlacement(banners);
    });
    const bannerDetailsWithCustomizedKeys = getCustomizedScreenKeys(groupBannerByScreens);
    return bannerDetailsWithCustomizedKeys;
};
export const PREVIEW_BANNER_PARAM = "previewBannerName";
export const isPreviewBanner = (bannerName) => {
    if (!bannerName)
        return false;
    try {
        if (typeof window !== "undefined" && window.location?.href) {
            const url = new URL(window.location.href);
            const previewBannerNameParam = url.searchParams.get(PREVIEW_BANNER_PARAM);
            return previewBannerNameParam === bannerName;
        }
        return false;
    }
    catch (error) {
        cmsLogger.error("Error checking preview banner:", error);
        return false;
    }
};
export const getCustomizedScreenKeys = (banners) => {
    const bannerDetailsWithCustomizedKeys = {};
    Object.keys(banners).forEach((key) => {
        const screenKey = CONTENTFUL_SCREENS[key];
        if (!screenKey)
            return;
        bannerDetailsWithCustomizedKeys[screenKey] = banners[key];
    });
    return bannerDetailsWithCustomizedKeys;
};
export const getGroupedBannersByPlacement = (banners) => {
    const groupedBanners = {};
    banners.forEach((banner) => {
        const bannerPlacement = banner?.placement;
        const bannerForPlacement = groupedBanners[bannerPlacement] || [];
        bannerForPlacement.push(getCustomizeBannerStructure(banner));
        groupedBanners[bannerPlacement] = bannerForPlacement;
    });
    return groupedBanners;
};
/**
 * Short, url-safe name for each tier. Deliberately not the tier's display label: the label is
 * prose that can be reworded at any time, and it would put an apostrophe and spaces in a url.
 */
const TIER_SLUGS = {
    [SubscriptionTiers.MINIMAL]: "minimal",
    [SubscriptionTiers.BASIC]: "basic",
    [SubscriptionTiers.PREMIUM]: "premium",
    [SubscriptionTiers.FREE]: "free",
    [SubscriptionTiers.ANONYMOUS]: "anonymous",
};
const PAID_TIER_SLUGS = [
    TIER_SLUGS[SubscriptionTiers.MINIMAL],
    TIER_SLUGS[SubscriptionTiers.BASIC],
    TIER_SLUGS[SubscriptionTiers.PREMIUM],
];
/**
 * Cache key for a signed-in caller's banner request.
 *
 * Billing cycle is appended for paid tiers, which are the only ones whose banners can differ by it.
 * The set stays small on purpose: each audience gets its own cache entry without the entry count
 * growing per user, which is what would evict the service worker's small budget.
 */
export const getBannerSegment = (subscriptionTier, billingCycle) => {
    const tierSlug = TIER_SLUGS[subscriptionTier];
    if (billingCycle && PAID_TIER_SLUGS.includes(tierSlug)) {
        return `${tierSlug}-${billingCycle}`;
    }
    return tierSlug;
};
export const getBannerAPIUrl = (payload) => {
    const { bannerNames, preview, isSignedIn, segment, applicationId } = payload;
    if (preview) {
        return `/cms/banners?names=${bannerNames}&preview=${preview}`;
    }
    // A visitor with no session is served by an application-scoped path that carries no caller
    // identity, so one response serves every anonymous visitor, can be held in a shared cache, and
    // can be warmed by the service worker before the app asks for it.
    if (!isSignedIn && applicationId) {
        return `/v2/applications/${encodeURIComponent(applicationId)}/banners`;
    }
    // Everything else is caller-scoped. The segment keeps one audience's cached response from
    // satisfying another's request, in the service worker cache and the browser's own alike.
    return segment ? `/cms/banners?segment=${encodeURIComponent(segment)}` : `/cms/banners`;
};
export const getUnseenSessionBanners = (viewedBanners, banners) => {
    if (!viewedBanners)
        return [];
    const filteredBanners = banners?.filter((banner) => !viewedBanners?.includes(banner?.name));
    return filteredBanners;
};
export const getUpdatedViewedBanner = (viewedBanners, banners = []) => {
    const alreadyViewedBanners = [...viewedBanners];
    banners.forEach((banner) => {
        alreadyViewedBanners.push(banner?.name);
    });
    return alreadyViewedBanners;
};
export const addBannersToStorage = async (banners = []) => {
    const storedBanners = await getBannersFromLocalStorage();
    banners?.forEach((banner) => {
        storedBanners[banner?.name] = banner?.version;
    });
    const stringifiedBanners = JSON.stringify(storedBanners);
    await localStorageService.setItem(CMS_BANNERS_STORAGE_KEY, stringifiedBanners);
};
export const getBannersFromLocalStorage = async () => {
    const storedBanners = await localStorageService.getItem(CMS_BANNERS_STORAGE_KEY);
    let parsedBanners = {};
    if (storedBanners) {
        parsedBanners = JSON.parse(storedBanners);
    }
    return parsedBanners;
};
export const setBannersInLocalStorage = async (banners) => {
    const stringifiedBanners = JSON.stringify(banners);
    await localStorageService.setItem(CMS_BANNERS_STORAGE_KEY, stringifiedBanners);
};
